// Portal "My roadmap" API client (kanban 5895aca3). Backend:
// app/routes/my_roadmap.py — who I am on the roadmap (names, teams), my
// questions and the initiatives I am named on; the whole roadmap itself is
// read through roadmapReports.ts (roadmap:read, owner ask 2026-10-15).
import api from './api';
import type {
  ClarifyingQuestion,
  InitiativeEvidence,
  InitiativePriority,
  InitiativeStatus,
  OrgChartResponse,
  QuestionIdentity,
  QuestionResponse,
  QuestionResponseRequest,
  ScheduleHealth,
} from './roadmapReports';

export interface MyInitiative {
  initiative_key: string;
  title: string;
  status: string;
  health: string;
  target_date: string | null;
  teams: string[];
  owner_team?: string | null;
  related_teams?: string[];
  owner: string | null;
  people: string[];
  summary: string;
}

export interface MyRoadmapResponse {
  me: string[];
  /** The task forces the people directory places me on (the "Mine" filter keys on these too). */
  teams: string[];
  questions: ClarifyingQuestion[];
  initiatives: MyInitiative[];
  /** Leadership-only: present for admins / exec readers, null otherwise. */
  org: OrgChartResponse | null;
  report_date: string | null;
}

// ── Meetings (kanban 108573ba) ───────────────────────────────────────────
// GET /my-roadmap/meetings: the meeting series this person may open and the
// initiatives + questions their agendas are built from. The backend
// (services/roadmap/meeting_series.py) owns the series registry and the
// gate: leadership sees every series, everyone else the series of the
// teams the people directory puts them on or whose room names them.

export interface MeetingSeries {
  id: string;
  name: string;
  /** 0 = Sunday … 6 = Saturday (JS getDay). */
  weekday: number;
  /** Local wall-clock start, for the label. */
  time: string;
  /** Teams whose initiatives belong on this agenda; empty = the whole roadmap. */
  teams: string[];
  /** Who is usually in the room, in roadmap spelling. */
  room: string[];
}

/** An initiative as an agenda needs it (a subset of InitiativeState, so a
 * full InitiativeState is one too). */
export interface MeetingInitiative {
  initiative_key: string;
  title: string;
  status: InitiativeStatus;
  health: ScheduleHealth;
  target_date: string | null;
  started_on: string | null;
  teams: string[];
  owner: string | null;
  people: string[];
  depends_on: string[];
  blocks: string[];
  external_blocker: string | null;
  priority: InitiativePriority | null;
  summary: string;
  evidence?: InitiativeEvidence[];
  updated_at: string;
}

export interface MyMeetingsResponse {
  me: string[];
  series: MeetingSeries[];
  initiatives: MeetingInitiative[];
  questions: ClarifyingQuestion[];
  report_date: string | null;
}

export const myRoadmapApi = {
  get: async (): Promise<MyRoadmapResponse> => (await api.get<MyRoadmapResponse>('/my-roadmap')).data,
  meetings: async (seriesOnly = false): Promise<MyMeetingsResponse> =>
    (await api.get<MyMeetingsResponse>('/my-roadmap/meetings', { params: seriesOnly ? { series_only: true } : undefined })).data,
  respond: async (req: QuestionResponseRequest): Promise<QuestionResponse> =>
    (
      await api.post<QuestionResponse>('/my-roadmap/respond', {
        initiative_key: req.initiative_key,
        question: req.question,
        kind: req.kind,
        answer: req.kind === 'answered' ? req.answer ?? null : null,
      })
    ).data,
  withdraw: async (id: QuestionIdentity): Promise<void> => {
    await api.delete('/my-roadmap/respond', {
      params: { initiative_key: id.initiative_key ?? undefined, question: id.question },
    });
  },
};
